import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { convertApplicationsToString } from '../../../common/Briefing';
import { Language } from '../../../common/CountryMarketConfig';
import { CRM_MAX_FIELD_LENGTH } from '../../../common/config';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { Link } from '../../../components/Link';
import { ToggleButton } from '../../../components/ToggleButton';
import { ToggleButtonGroup } from '../../../components/ToggleButtonGroup';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { getAvailableTaxonomiesFromState } from '../../../controllers/TaxonomyController/TaxonomyHelper';
import { useTaxonomyState } from '../../../controllers/TaxonomyController/TaxonomyState';
import {
  findIndexOfKeyInProductApplications,
  getProductApplicationKeysForIndex,
  getTaxonomyByPreferredLocale,
  isKeyInProductApplicationsAtIndex,
  isTaxonomyWithKeyMultiselect,
} from '../../../lib/briefing';
import { noOp } from '../../../lib/utils';
import {
  StepProps,
  TaxonomyWithKey,
  TaxonomyWithKeyMultiselect,
} from '../../../types';
import { useBriefingState } from '../BriefingState';
import {
  getApplicationKeysByParentKey,
  getMaxApplicationLevel,
  sortTaxonomiesByName,
} from './utils';

const applicationHeadingClasses = 'ml-8 mr-2 whitespace-nowrap';

export const StepApplication: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const [
    { branch, productApplications, productApplicationsOther },
    { setProductApplications, setProductApplicationsOther },
  ] = useBriefingState();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const [maxApplicationLevel, setMaxApplicationLevel] = useState(0);
  const [taxonomyState] = useTaxonomyState();

  const [remainingCharacterLimit, setRemainingCharacterLimit] =
    useState(CRM_MAX_FIELD_LENGTH);

  const [isProductApplicationOtherValid, setIsProductApplicationOtherValid] =
    useState(true);
  const isStepValid = useMemo(
    () => isProductApplicationOtherValid,
    [isProductApplicationOtherValid],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const availableTaxonomies = useMemo(
    () =>
      Object.entries(
        getAvailableTaxonomiesFromState(
          taxonomyState,
          countryMarketConfig,
          i18n.language as Language,
        ),
      )
        .filter(
          ([key]) =>
            countryMarketConfig?.taxonomyMarketAbbreviaton !== 'AG' ||
            key.startsWith('A_AG_001'),
        )
        .reduce(
          (taxonomies, [key, values]) => ({
            ...taxonomies,
            [key]: values,
          }),
          {},
        ),
    [countryMarketConfig, i18n.language, taxonomyState],
  );

  useEffect(() => {
    const parentKey = productApplications[0]?.key.toString() || '';
    const nextOptions = getApplicationKeysByParentKey(
      parentKey,
      availableTaxonomies,
    );

    // If there are no further options (aside from Other), don't show any more and allow
    // the user to continue. Part 2 of this functionality is in Briefing:getMandatoryApplications()
    if (nextOptions.length === 0) {
      setMaxApplicationLevel(1);
    } else {
      setMaxApplicationLevel(
        getMaxApplicationLevel(
          availableTaxonomies,
          countryMarketConfig?.taxonomyMarketAbbreviaton,
        ),
      );
    }
  }, [productApplications, availableTaxonomies, countryMarketConfig]);

  useEffect(() => {
    const stringifiedApplicationsData =
      convertApplicationsToString(productApplications);
    setRemainingCharacterLimit(
      CRM_MAX_FIELD_LENGTH - stringifiedApplicationsData.length,
    );
  }, [productApplications, productApplicationsOther]);

  const resetApplicationAndSubapplications = useCallback(
    (applicationKey: string) => {
      const tempSelectedApplications = [...productApplications];
      const applicationIndex = tempSelectedApplications.findIndex(
        (selectedApplication) => {
          if (isTaxonomyWithKeyMultiselect(selectedApplication)) {
            return Object.values(selectedApplication).some(
              (pa) => pa.key === applicationKey,
            );
          } else {
            return selectedApplication.key === applicationKey;
          }
        },
      );

      const productApplication = tempSelectedApplications[applicationIndex];
      if (isTaxonomyWithKeyMultiselect(productApplication)) {
        const tempProductApplication = Object.values(productApplication)
          .filter((pa) => pa.key !== applicationKey)
          .reduce((acc, value, index) => {
            acc[index.toString()] = value;
            return acc;
          }, {} as TaxonomyWithKeyMultiselect);
        if (Object.values(tempProductApplication).length === 0) {
          tempSelectedApplications.splice(
            applicationIndex,
            tempSelectedApplications.length - applicationIndex,
          );
        } else {
          tempSelectedApplications[applicationIndex] = tempProductApplication;
        }
      } else {
        tempSelectedApplications.splice(
          applicationIndex,
          tempSelectedApplications.length - applicationIndex,
        );
      }
      dispatch(setProductApplications(tempSelectedApplications));
    },
    [productApplications, dispatch, setProductApplications],
  );

  const generateApplicationButtonsForParentByLevel = useCallback(
    (
      parentKey: string,
      index: number,
      isMultiselect: boolean,
      toggleDisable?: boolean,
    ) => {
      if (!Object.keys(availableTaxonomies).length) return [];

      return getApplicationKeysByParentKey(parentKey, availableTaxonomies)
        .map(
          (key) =>
            ({
              ...getTaxonomyByPreferredLocale(
                key,
                availableTaxonomies,
                i18n.language,
              ),
              key,
            } as TaxonomyWithKey),
        )
        .filter(({ data, locale }) => !!data && !!locale)
        .sort(sortTaxonomiesByName)
        .concat({
          data: t('labels:other'),
          key: 'other',
          locale: i18n.language,
        })
        .filter(
          ({ key }) =>
            productApplications.length === index ||
            isKeyInProductApplicationsAtIndex(
              key,
              productApplications,
              index,
            ) ||
            isMultiselect,
        )
        .filter(({ data }) => data !== 'Others')
        .map((application) => (
          <ToggleButton
            dataTest={`application-button`}
            key={application.key}
            text={application.data}
            value={application.key}
            disableToggle={toggleDisable || false}
            on={
              findIndexOfKeyInProductApplications(
                application.key,
                productApplications,
              ) > -1
            }
            onToggle={(on: boolean) => {
              if (!on) {
                resetApplicationAndSubapplications(application.key);

                if (application.key === 'other') {
                  dispatch(setProductApplicationsOther(null));
                  setIsProductApplicationOtherValid(true);
                }
                return;
              }

              if (!isMultiselect) {
                dispatch(
                  setProductApplications(
                    productApplications.concat([application]),
                  ),
                );
                dispatch(setProductApplicationsOther(null));
                setIsProductApplicationOtherValid(true);
                return;
              }
              const tempProductApplications = productApplications.map((pa) =>
                Object.assign({}, pa),
              );
              const tempProductApplication = tempProductApplications[index];
              if (isTaxonomyWithKeyMultiselect(tempProductApplication)) {
                tempProductApplication[
                  Object.keys(tempProductApplication).length
                ] = application;
                tempProductApplications[index] = tempProductApplication;
              } else {
                tempProductApplications[index] = {
                  '0': application,
                };
              }
              dispatch(setProductApplications(tempProductApplications));
            }}
          />
        ));
    },
    [
      availableTaxonomies,
      dispatch,
      productApplications,
      setProductApplications,
      setProductApplicationsOther,
      resetApplicationAndSubapplications,
      i18n.language,
      t,
    ],
  );

  const generateMarketSpecificHeadingByLevel = useCallback(
    (level: number, market: string, headingState: string) => {
      let headingByMarket;
      switch (level) {
        case 0:
          headingByMarket = market === 'AG' ? 'product' : headingState;
          break;

        case 1:
          headingByMarket = market === 'AG' ? 'technology' : headingState;
          break;

        case 2:
          headingByMarket = market === 'HCIC' ? 'endUse' : headingState;
          break;

        default:
          headingByMarket = headingState;
          break;
      }

      return (
        <Heading
          level="h3"
          margin={0}
          text={t(`topics:application:levels:${level}:${headingByMarket}`)}
        />
      );
    },
    [t],
  );

  const editButton = useCallback(
    (level: number) => (
      <div className={applicationHeadingClasses}>
        <Link
          label={t('linksOrButtons:edit')}
          icon="Edit"
          onClick={() => {
            resetApplicationAndSubapplications(
              getProductApplicationKeysForIndex(
                productApplications,
                level,
              )?.[0] ?? '',
            );
          }}
        />
      </div>
    ),
    [productApplications, resetApplicationAndSubapplications, t],
  );

  // For AgriEnvironmental, automatically dispatch level 0 application ('Crop Protection')
  useEffect(() => {
    if (
      countryMarketConfig?.taxonomyMarketAbbreviaton === 'AG' &&
      productApplications.length < 1
    ) {
      dispatch(
        setProductApplications([
          { data: 'Crop protection', key: 'A_AG_001', locale: 'en_us' },
        ]),
      );
    }
  }, [
    dispatch,
    countryMarketConfig,
    setProductApplications,
    productApplications.length,
  ]);

  const generateSelectedButton = useCallback(
    (level: number, isMultiselect: boolean) => {
      return (
        <div
          className="flex flex-col justify-between w-full space-y-4 sm:w-2/3 md:w-full"
          key={level}
        >
          <div className="flex justify-between">
            {generateMarketSpecificHeadingByLevel(
              level,
              `${countryMarketConfig?.taxonomyMarketAbbreviaton}`,
              'selected',
            )}
            {countryMarketConfig?.taxonomyMarketAbbreviaton !== 'AG' &&
              editButton(level)}
          </div>
          <ToggleButtonGroup
            initialToggles={
              getProductApplicationKeysForIndex(productApplications, level) ??
              []
            }
          >
            {generateApplicationButtonsForParentByLevel(
              level === 0
                ? `A_${countryMarketConfig?.taxonomyMarketAbbreviaton}`
                : getProductApplicationKeysForIndex(
                    productApplications,
                    level - 1,
                  )?.[0] ?? '',
              level,
              isMultiselect,
              // Pass boolean for disabling the toggle on AgriEnvironmental for level 0
              countryMarketConfig?.taxonomyMarketAbbreviaton === 'AG' &&
                level === 0,
            )}
          </ToggleButtonGroup>
        </div>
      );
    },
    [
      countryMarketConfig,
      editButton,
      generateApplicationButtonsForParentByLevel,
      generateMarketSpecificHeadingByLevel,
      productApplications,
    ],
  );

  const generateUnselectedButton = useCallback(
    (
      level: number,
      isMultiselect: boolean,
      levelHasSelectedOption: boolean,
      currentLevelIsOther: boolean,
    ) => {
      const isLevelTwoAndHCIC =
        level === 2 &&
        countryMarketConfig?.eLabMarketSlug === 'HomeCareIndustrialCleaning';
      return (
        <div key={level}>
          <TemplateFlexGridContainer.BriefingStepSection>
            <div className="flex">
              <div>
                {generateMarketSpecificHeadingByLevel(
                  level,
                  `${countryMarketConfig?.taxonomyMarketAbbreviaton}`,
                  'normal',
                )}
                {isMultiselect && (
                  <p className="text-xs text-gray-600">
                    {t(`labels:multiSelectHint`)}
                  </p>
                )}
              </div>
              {levelHasSelectedOption && editButton(level)}
            </div>
            {/* This container div is here to stop weird Safari bug: AZEELAB-111 */}
            <div>
              <ToggleButtonGroup
                multi={isMultiselect}
                initialToggles={
                  getProductApplicationKeysForIndex(
                    productApplications,
                    level,
                  ) ?? []
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8">
                  {generateApplicationButtonsForParentByLevel(
                    level === 0
                      ? `A_${countryMarketConfig?.taxonomyMarketAbbreviaton}`
                      : getProductApplicationKeysForIndex(
                          productApplications,
                          level - 1,
                        )?.[0] ?? '',
                    level,
                    isMultiselect,
                  )}
                </div>
              </ToggleButtonGroup>
            </div>
          </TemplateFlexGridContainer.BriefingStepSection>
          {currentLevelIsOther && (
            <div className="xs:mt-5 sm:mt-8">
              <TemplateFlexGridContainer.BriefingStepSection xsSpaceY={1}>
                <div className="w-full md:w-6/12">
                  <Heading
                    level="h4"
                    margin={0}
                    text={
                      isLevelTwoAndHCIC
                        ? t(`topics:application:levels:2:specifyEndUse`)
                        : t(`topics:application:levels:${level}:other`)
                    }
                  />
                  <Input
                    initialValue={productApplicationsOther}
                    placeholder={
                      isLevelTwoAndHCIC
                        ? t(`topics:application:levels:2:endUsePlaceholder`)
                        : t(
                            `topics:application:levels:${level}:otherPlaceholder`,
                          )
                    }
                    maxLength={remainingCharacterLimit}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      dispatch(setProductApplicationsOther(e.target.value))
                    }
                    onValidate={setIsProductApplicationOtherValid}
                  />
                </div>
              </TemplateFlexGridContainer.BriefingStepSection>
            </div>
          )}
        </div>
      );
    },
    [
      countryMarketConfig,
      dispatch,
      editButton,
      generateApplicationButtonsForParentByLevel,
      generateMarketSpecificHeadingByLevel,
      productApplications,
      productApplicationsOther,
      remainingCharacterLimit,
      setProductApplicationsOther,
      t,
    ],
  );

  const applicationButtons: {
    selectedButtons: JSX.Element[];
    unselectedButtons: JSX.Element[];
  } = useMemo(() => {
    const tempSelectedButtons: JSX.Element[] = [];
    const tempUnselectedButtons: JSX.Element[] = [];
    [
      ...Array(Math.min(productApplications.length + 1, maxApplicationLevel)),
    ].forEach((_, level) => {
      const previousLevelIsOther = isKeyInProductApplicationsAtIndex(
        'other',
        productApplications,
        level - 1,
      );
      const currentLevelIsOther = isKeyInProductApplicationsAtIndex(
        'other',
        productApplications,
        level,
      );

      if (previousLevelIsOther) {
        return null;
      }

      const levelHasSelectedOption = productApplications.length > level;
      const isNotFinalLevel = level !== maxApplicationLevel - 1;

      const isCharacteristicsLevel = level === 2;
      const useMultiselect =
        isCharacteristicsLevel &&
        countryMarketConfig?.taxonomyMarketAbbreviaton === 'FH';

      if (
        levelHasSelectedOption &&
        isNotFinalLevel &&
        !currentLevelIsOther &&
        !useMultiselect
      ) {
        tempSelectedButtons.push(generateSelectedButton(level, useMultiselect));
      } else {
        tempUnselectedButtons.push(
          generateUnselectedButton(
            level,
            useMultiselect,
            levelHasSelectedOption,
            currentLevelIsOther,
          ),
        );
      }
    });

    return {
      selectedButtons: tempSelectedButtons,
      unselectedButtons: tempUnselectedButtons,
    };
  }, [
    productApplications,
    maxApplicationLevel,
    countryMarketConfig,
    generateSelectedButton,
    generateUnselectedButton,
  ]);

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:application:heading:${branch}`)}
        optionalField={mandatoryFields['application']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody
        spaceY={5}
        mdSpaceY={7}
        lgSpaceY={9}
      >
        {applicationButtons.selectedButtons.length > 0 && (
          <div
            data-test="selected-buttons-container"
            className={`flex flex-col space-y-6 ${
              applicationButtons.selectedButtons.length > 1 ? 'md:flex-row' : ''
            } md:space-y-0`}
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
              {applicationButtons.selectedButtons}
            </div>
          </div>
        )}
        <div
          data-test="unselected-buttons-container"
          className="flex flex-col mt-4 space-y-6"
        >
          {applicationButtons.unselectedButtons}
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
